import { Component, ElementRef, Host, Input, OnDestroy, OnInit, Optional, Self, ViewChild, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { CacheMap } from '@app/classes';
import { PhonePrefix } from '@app/models';
import { DictionaryService } from '@app/services';
import { TranslateService } from '@ngx-translate/core';
import { FormUtils, PhoneNumber, Utils } from 'app/core/utils';
import { DEFAULT_COUNTRY_CODE } from 'app/shared/constants';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-phone-number-input',
  templateUrl: './phone-number-input.component.html',
  styleUrls: ['./phone-number-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => PhoneNumberInputComponent),
    },
  ],
})
export class PhoneNumberInputComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @ViewChild('searchInput') searchInput: ElementRef;

  @Input() phonePrefixes: CacheMap<PhonePrefix>;
  @Input() isRequired: boolean = false;

  phoneNumber: FormControl = new FormControl('');
  selectedPhonePrefix?: PhonePrefix;
  selectedPhonePrefixBak?: PhonePrefix;
  search: FormControl = new FormControl('');
  subscriptions: Subscription[] = [];

  isDisabled: boolean = false;

  isInvalid: { warning?: boolean; error?: boolean } = {};

  constructor(private dictionary: DictionaryService, private translate: TranslateService) {}

  get filteredPrefixes() {
    const search = this.search.value;
    return this.phonePrefixes
      .with(this.selectedPhonePrefixBak?.phonePrefixId)
      .filter(
        (prefix) =>
          prefix.code.toLowerCase().includes(search.toLowerCase()) ||
          prefix.value.toLowerCase().includes(search.toLowerCase()) ||
          this.translate.instant(`DB.COUNTRY.${prefix.code}`).toLowerCase().includes(search.toLowerCase())
      );
  }

  private _onChange = (_: PhoneNumber) => undefined;
  registerOnChange(fn: (_: any) => void): void {
    this._onChange = fn;
  }

  onTouched() {
    this._onTouched();
  }
  private _onTouched = () => undefined;
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (isDisabled) this.phoneNumber.disable();
    else this.phoneNumber.enable();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const isEmpty = control.value.phoneNumber == '' || control.value.phoneNumber == null;
    if (!isEmpty && !control.touched) {
      control.markAsTouched();
      control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }
    if (!control.touched) return;
    this.isRequired = control.hasValidator(Validators.required);
    if (this.isRequired && isEmpty) {
      this.isInvalid = { error: true };
      return { required: true };
    }
    if (control.value.phoneNumber?.length > 50) {
      this.isInvalid = { error: true };
      return { maxlength: true };
    }
    const prefix = this.phonePrefixes?.with(control.value.phonePrefixId).get(control.value.phonePrefixId);
    const valid = FormUtils.validatePhoneNumber(control.value.phoneNumber, prefix);
    if (!valid && !isEmpty) {
      this.isInvalid = { warning: true };
      return null;
    }
    this.isInvalid = {};
    return null;
  }

  writeValue(value: PhoneNumber): void {
    this.phoneNumber.patchValue(value.phoneNumber);
    if (value.phoneNumber != '' && value.phoneNumber != null) this._onTouched();
    this.patchPhonePrefixValue(value.phonePrefixId).then(() => this.changeSelectedPrefix(this.selectedPhonePrefix));
  }

  ngOnDestroy(): void {
    this.subscriptions = Utils.unsubscribe(this.subscriptions);
  }

  ngOnInit() {
    const phoneSub = this.phoneNumber.valueChanges.subscribe((value) =>
      this._onChange({
        phoneNumber: value,
        phonePrefixId: this.selectedPhonePrefix?.phonePrefixId,
      })
    );
    this.subscriptions.push(phoneSub);
  }

  async patchPhonePrefixValue(phonePrefixId: string) {
    this.phonePrefixes ??= await this.dictionary.getPhonePrefixes();
    this.selectedPhonePrefix = (() => {
      if (phonePrefixId) return this.phonePrefixes.with(phonePrefixId).get(phonePrefixId);
      else {
        const localePrefix = this.phonePrefixes.findByCode(DEFAULT_COUNTRY_CODE);
        if (localePrefix) return localePrefix;

        return null;
      }
    })();
    if (this.selectedPhonePrefix?.deleted) this.selectedPhonePrefixBak = this.selectedPhonePrefix;
  }

  changeSelectedPrefix(prefix: PhonePrefix) {
    this.selectedPhonePrefix = prefix;
    this._onChange({ phoneNumber: this.phoneNumber.value, phonePrefixId: this.selectedPhonePrefix.phonePrefixId });
  }

  onToggle(isOpen: 0 | 1) {
    if (isOpen) setTimeout(() => this.searchInput.nativeElement.focus());
  }
}
